import { useEffect, useState } from 'react'
import { Toggle } from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import { useAccountStore, useAssetStore, useUserStore } from 'store'
import { useShallow } from 'zustand/react/shallow'

/**
 * Inline Protection toggle for an Ad Accounts table row (DEV-2352 §2.1).
 * Optimistic — flips immediately and reverts if the write fails. Reuses the
 * existing `updateSettingsAccountProtection` store action / endpoint.
 */
export const ProtectionToggleCell = ({
	adAccount
}: {
	adAccount: AdAccountDTO
}) => {
	const user = useUserStore((s) => s.user)
	const { updateSettingsAccountProtection, demoMode } = useAssetStore(
		useShallow((s) => ({
			updateSettingsAccountProtection: s.updateSettingsAccountProtection,
			demoMode: s.demoMode
		}))
	)
	const { userAccess } = useAccountStore(
		useShallow((s) => ({ userAccess: s.userAccess }))
	)

	const [active, setActive] = useState(adAccount.status === 'ACTIVE')

	useEffect(() => {
		setActive(adAccount.status === 'ACTIVE')
	}, [adAccount.status])

	const disabled = demoMode || userAccess(user?.id || '') === 'READ'

	const handleToggle = async () => {
		const next = !active
		setActive(next)
		try {
			await updateSettingsAccountProtection(
				adAccount.account_id,
				adAccount.id,
				{ active: next }
			)
		} catch {
			setActive(!next)
		}
	}

	return (
		<div className="flex items-center gap-2">
			<Toggle
				id={`protection-row-${adAccount.id}`}
				size="md"
				checked={active}
				disabled={disabled}
				onChange={handleToggle}
			/>
			<span className="text-sm text-t-secondary">
				{active ? 'On' : 'Off'}
			</span>
		</div>
	)
}
